import { useEffect, useState } from "react";
import client from "../lib/api";
import { money, DUE_FILTERS, dueOf, daysOverdue, applyDueView, fmtDate } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";
import { CurrencyDollar, CheckCircle, Clock, DownloadSimple, FilePdf, FileCsv, PencilSimple, Ticket, Bank, PaperPlaneTilt, ChartLineUp, ArrowDown, ArrowUp, CreditCard, CalendarBlank, ArrowsDownUp, HandCoins, Plus, CalendarCheck } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import BillingTable from "../components/BillingTable";
import DiscountsPanel from "../components/DiscountsPanel";
import PayoutHistory from "../components/PayoutHistory";
import EmbeddedPayouts from "../components/EmbeddedPayouts";
import AdminPayoutAccounts from "../components/AdminPayoutAccounts";
import FinanceOverview from "../components/FinanceOverview";
import PayoutSchedule from "../components/PayoutSchedule";
import CommissionsTracker from "../components/CommissionsTracker";
import PhotographerPayDefaults from "../components/PhotographerPayDefaults";
import TestPaymentTools from "../components/TestPaymentTools";

const QTR_NAMES = ["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"];

// Options: All time, last 12 months, last 4 quarters.
function EXPORT_PERIODS() {
  const opts = [{ value: "all", label: "All time" }];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    opts.push({ value: `m:${d.getFullYear()}-${mm}`, label: d.toLocaleString("en-US", { month: "long", year: "numeric" }) });
  }
  let qy = now.getFullYear(), qq = Math.floor(now.getMonth() / 3);
  for (let i = 0; i < 4; i++) {
    opts.push({ value: `q:${qy}-${qq + 1}`, label: `Q${qq + 1} ${qy} (${QTR_NAMES[qq]})` });
    qq -= 1; if (qq < 0) { qq = 3; qy -= 1; }
  }
  return opts;
}

function periodRange(val) {
  if (!val || val === "all") return {};
  const [kind, spec] = val.split(":");
  const pad = (n) => String(n).padStart(2, "0");
  if (kind === "m") {
    const [y, m] = spec.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(last)}` };
  }
  if (kind === "q") {
    const [y, q] = spec.split("-").map(Number);
    const sm = (q - 1) * 3 + 1, em = sm + 2;
    const last = new Date(y, em, 0).getDate();
    return { start: `${y}-${pad(sm)}-01`, end: `${y}-${pad(em)}-${pad(last)}` };
  }
  return {};
}


const PAY_TONES = {
  emerald: { cls: "surface-kpi--emerald", medal: "bg-[#E6EFEA] text-[#1A4331] border-[#2D6A4F]/30", value: "#1A4331" },
  bronze: { cls: "surface-kpi--bronze", medal: "bg-[#F5ECE3] text-[#8A5028] border-[#B87333]/35", value: "#8A5028" },
  oxblood: { cls: "surface-kpi--oxblood-accent", medal: "bg-[#4A0000]/8 text-[#4A0000] border-[#4A0000]/20", value: "#4A0000" },
};
function Stat({ label, value, icon: Icon, variant = "oxblood" }) {
  const t = PAY_TONES[variant] || PAY_TONES.oxblood;
  return (
    <div className={`surface-kpi ${t.cls} p-6`} data-testid="pay-stat">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[#5C5A58] eyebrow">{label}</span>
        <span className={`h-8 w-8 rounded-full flex items-center justify-center border ${t.medal}`}><Icon size={16} weight="duotone" /></span>
      </div>
      <p className="font-display text-3xl tabular-nums" style={{ color: t.value }}>{money(value)}</p>
    </div>
  );
}

function PayoutView({ isAdmin }) {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState("");
  const [period, setPeriod] = useState("all");
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ edited: false, bonus: "", bonus_note: "", paid: false });
  const [saving, setSaving] = useState(false);
  const [connect, setConnect] = useState(null);      // photographer's own payout status
  const [showOnboard, setShowOnboard] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [payoutRow, setPayoutRow] = useState(null);  // admin: row being paid out
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payingOut, setPayingOut] = useState(false);
  const [payoutVersion, setPayoutVersion] = useState(0);
  const [manualRow, setManualRow] = useState(null);
  const [manualAmount, setManualAmount] = useState("");
  const [manualMethod, setManualMethod] = useState("Cash");
  const [manualNote, setManualNote] = useState("");
  const [manualFull, setManualFull] = useState(true);
  const [savingManual, setSavingManual] = useState(false);
  const [dueMode, setDueMode] = useState("all");
  const [dueSort, setDueSort] = useState("none");
  const [adjRow, setAdjRow] = useState(null);
  const [adjType, setAdjType] = useState("addition");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [savingAdj, setSavingAdj] = useState(false);

  const load = () => client.get("/pay/summary").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!isAdmin) client.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {});
  }, [isAdmin]);

  const refetchStatus = () => client.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {});

  const openPayout = (r) => { setPayoutAmount(String(r.amount || 0)); setPayoutRow(r); };
  const doPayout = async () => {
    const amt = Math.round((parseFloat(payoutAmount) || 0) * 100) / 100;
    if (amt <= 0) return toast.error("Enter a payout amount greater than $0");
    setPayingOut(true);
    try {
      const r = await client.post("/connect/payout", { property_id: payoutRow.property_id, amount: amt });
      toast.success(`Paid ${money(r.data.amount)} to ${payoutRow.photographer}`);
      setPayoutRow(null);
      setPayoutVersion((v) => v + 1);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Payout failed"); }
    finally { setPayingOut(false); }
  };

  const openManual = (r) => {
    setManualAmount(String(r.amount || 0)); setManualMethod("Cash");
    setManualNote(""); setManualFull(true); setManualRow(r);
  };
  const doManual = async () => {
    const amt = Math.round((parseFloat(manualAmount) || 0) * 100) / 100;
    if (amt <= 0) return toast.error("Enter an amount greater than $0");
    setSavingManual(true);
    try {
      await client.post("/connect/payout/manual", {
        property_id: manualRow.property_id, amount: amt,
        method: manualMethod, note: manualNote || null, settle_full: manualFull,
      });
      toast.success(`Recorded ${money(amt)} paid to ${manualRow.photographer} via ${manualMethod}`);
      setManualRow(null);
      setPayoutVersion((v) => v + 1);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record payment"); }
    finally { setSavingManual(false); }
  };

  const openAdj = (r) => { setAdjType("addition"); setAdjAmount(""); setAdjReason(""); setAdjRow(r); };
  const doAdj = async () => {
    const amt = Math.round((parseFloat(adjAmount) || 0) * 100) / 100;
    if (amt <= 0) return toast.error("Enter an amount greater than $0");
    if (!adjReason.trim()) return toast.error("A reason is required");
    setSavingAdj(true);
    try {
      await client.post(`/finance/payables/${adjRow.property_id}/adjust`, { type: adjType, amount: amt, reason: adjReason.trim() });
      toast.success(`${adjType === "addition" ? "Addition" : "Deduction"} of ${money(amt)} applied`);
      setAdjRow(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply adjustment"); }
    finally { setSavingAdj(false); }
  };

  if (!data) return <p className="text-[#5C5A58]">Loading…</p>;

  const visibleRows = applyDueView(data.rows || [], dueMode, dueSort);

  const exportReport = async (fmt) => {
    setExporting(fmt);
    try {
      const { start, end } = periodRange(period);
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const r = await client.get(`/pay/summary/export.${fmt}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const suffix = period === "all" ? "summary" : period.replace(":", "-");
      a.href = url; a.download = `reelty-pay-${suffix}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch {
      toast.error("Could not export report");
    } finally { setExporting(""); }
  };

  const openEdit = (r) => {
    setForm({ edited: !!r.edited, bonus: r.bonus ? String(r.bonus) : "", bonus_note: r.bonus_note || "", paid: !!r.paid });
    setEditRow(r);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      await client.patch(`/properties/${editRow.property_id}`, {
        edited: form.edited,
        bonus: Math.max(0, parseFloat(form.bonus) || 0),
        bonus_note: form.bonus_note || "",
        paid: form.paid,
      });
      toast.success("Payout updated");
      setEditRow(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update payout"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="pay-panel">
      {!isAdmin && connect && !connect.onboarded && (
        <div className="space-y-4" data-testid="payout-onboard-wrap">
          <div className="border border-[#B08D57]/40 rounded-md p-5 bg-[#B08D57]/10 flex items-center justify-between flex-wrap gap-3" data-testid="payout-onboard-card">
            <div className="flex items-center gap-3">
              <Bank size={22} weight="duotone" className="text-[#8a6d0a]" />
              <div>
                <p className="font-display text-lg leading-tight">Get paid faster</p>
                <p className="text-xs text-[#5C5A58] max-w-md">Set up Stripe payouts right here to receive your pay straight to your bank. Your details stay securely with Stripe — you never leave Reelty.</p>
              </div>
            </div>
            <Button data-testid="payout-onboard-btn" onClick={() => setShowOnboard((v) => !v)} className="bg-[#B08D57] hover:bg-[#9A7A4A] text-[#1A1A1A]">
              <Bank size={16} className="mr-1.5" weight="fill" />{showOnboard ? "Hide setup" : connect.has_account ? "Finish payout setup" : "Set up payouts"}
            </Button>
          </div>
          {showOnboard && (
            <div className="border border-[#E9DDC6] rounded-md bg-white p-5" data-testid="payout-onboard-embed">
              <EmbeddedPayouts mode="onboarding" onDone={refetchStatus} />
            </div>
          )}
        </div>
      )}
      {!isAdmin && connect && connect.onboarded && (
        <div className="space-y-4" data-testid="payout-ready-wrap">
          <div className="border border-green-200 rounded-md p-4 bg-green-50 flex items-center justify-between flex-wrap gap-3" data-testid="payout-ready-card">
            <span className="flex items-center gap-2 text-sm text-green-800"><CheckCircle size={18} weight="fill" /> Stripe payouts are set up — your pay lands straight in your bank.</span>
            <Button variant="outline" data-testid="payout-manage-btn" onClick={() => setShowManage((v) => !v)} className="border-green-300 text-green-800 hover:bg-green-100">
              {showManage ? "Hide" : "Manage payouts"}
            </Button>
          </div>
          {showManage && (
            <div className="border border-[#E9DDC6] rounded-md bg-white p-5" data-testid="payout-manage-embed">
              <EmbeddedPayouts mode="manage" />
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Stat label="Total payouts" value={data.total} icon={CurrencyDollar} variant="oxblood" />
        <Stat label="Paid" value={data.paid} icon={CheckCircle} variant="emerald" />
        <Stat label="Outstanding" value={data.unpaid} icon={Clock} variant="bronze" />
      </div>
      <div className="flex items-center gap-2 flex-wrap" data-testid="payout-filter-bar">
        <span className="text-sm text-[#5C5A58] flex items-center gap-1"><CalendarBlank size={16} weight="duotone" />Due</span>
        <Select value={dueMode} onValueChange={setDueMode}>
          <SelectTrigger className="w-[170px]" data-testid="payout-due-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DUE_FILTERS.map((f) => <SelectItem key={f.value} value={f.value} data-testid={`payout-due-filter-${f.value}`}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" data-testid="payout-due-sort" onClick={() => setDueSort((s) => s === "asc" ? "desc" : s === "desc" ? "none" : "asc")} className="border-[#E9DDC6]">
          <ArrowsDownUp size={15} className="mr-1" />{dueSort === "asc" ? "Soonest first" : dueSort === "desc" ? "Latest first" : "Sort by due"}
        </Button>
        <span className="text-xs text-[#9A968E]" data-testid="payout-visible-count">{visibleRows.length} of {data.rows.length}</span>
        {isAdmin && (
          <div className="flex items-center gap-2 ml-auto" data-testid="pay-export-row">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[190px]" data-testid="pay-export-period"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {EXPORT_PERIODS().map((p) => <SelectItem key={p.value} value={p.value} data-testid={`pay-export-period-${p.value}`}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-[#5C5A58] flex items-center gap-1"><DownloadSimple size={16} weight="duotone" />Export</span>
            <Button data-testid="export-pay-csv" onClick={() => exportReport("csv")} disabled={!!exporting} variant="outline" size="sm" className="border-[#E9DDC6]"><FileCsv size={16} className="mr-1.5" weight="duotone" />{exporting === "csv" ? "…" : "CSV"}</Button>
            <Button data-testid="export-pay-pdf" onClick={() => exportReport("pdf")} disabled={!!exporting} size="sm" className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white"><FilePdf size={16} className="mr-1.5" weight="duotone" />{exporting === "pdf" ? "…" : "PDF"}</Button>
          </div>
        )}
      </div>
      <div className="lux-panel overflow-x-auto">
        <table className="table-editorial text-sm min-w-[680px]" data-testid="pay-table">
          <thead>
            <tr className="bg-[#FAF9F6] text-left text-[#5C5A58]">
              <th className="p-4 font-medium">Property</th>
              {isAdmin && <th className="p-4 font-medium">Photographer</th>}
              <th className="p-4 font-medium">Base</th>
              <th className="p-4 font-medium">Edited</th>
              <th className="p-4 font-medium">Add-on</th>
              <th className="p-4 font-medium">Tip</th>
              <th className="p-4 font-medium">Total</th>
              <th className="p-4 font-medium">Due</th>
              <th className="p-4 font-medium">Status</th>
              {isAdmin && <th className="p-4 font-medium text-right">Edit</th>}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={isAdmin ? 10 : 8} className="p-8 text-center text-[#5C5A58]">No assigned shoots yet.</td></tr>}
            {data.rows.length > 0 && visibleRows.length === 0 && <tr><td colSpan={isAdmin ? 10 : 8} className="p-8 text-center text-[#5C5A58]">No payouts match this due-date filter.</td></tr>}
            {visibleRows.map((r, i) => (
              <tr key={r.property_id} className={r.paid ? "row-paid" : r.paid_amount > 0 ? "row-partial" : (daysOverdue(r) > 0 ? "row-overdue" : (i % 2 ? "bg-[#FBFAF7]" : "bg-white"))}>
                <td className="p-4 font-medium">{r.title}
                  {r.commission > 0 && <div className="text-xs text-[#8a6d0a] font-normal" data-testid={`pay-commission-${r.property_id}`}>+{money(r.commission)} link commission{isAdmin && r.commission_to ? ` → ${r.commission_to}` : ""}</div>}
                </td>
                {isAdmin && <td className="p-4">{r.photographer}</td>}
                <td className="p-4 font-mono-num">{money(r.flat_fee)}</td>
                <td className="p-4">{r.edited ? <span className="font-mono-num text-[#4A0000]">+{money(r.edit_fee)}</span> : <span className="text-[#5C5A58]">—</span>}</td>
                <td className="p-4">{r.bonus > 0 ? <span className="font-mono-num text-[#8a6d0a]" title={r.bonus_note || ""}>+{money(r.bonus)}</span> : <span className="text-[#5C5A58]">—</span>}</td>
                <td className="p-4">{r.tip > 0 ? <span className="font-mono-num text-[#2e7d32]">+{money(r.tip)}</span> : <span className="text-[#5C5A58]">—</span>}</td>
                <td className="p-4 font-mono-num font-semibold">{money(r.amount)}
                  {(r.pay_adj_total || r.standing_bonus > 0) ? (
                    <div className="text-xs font-normal text-[#5C5A58]" data-testid={`payout-adj-note-${r.property_id}`}>
                      {r.standing_bonus > 0 && <span className="text-[#8a6d0a]">+{money(r.standing_bonus)} standing</span>}
                      {r.pay_adj_total ? <span className={r.pay_adj_total > 0 ? "text-[#2e7d32]" : "text-[#E01E37]"}>{r.standing_bonus > 0 ? " · " : ""}{r.pay_adj_total > 0 ? "+" : "−"}{money(Math.abs(r.pay_adj_total))} adj</span> : null}
                    </div>
                  ) : null}
                </td>
                <td className="p-4" data-testid={`payout-due-${r.property_id}`}>
                  {dueOf(r) ? (
                    <>
                      <div className="text-sm">{fmtDate(dueOf(r))}</div>
                      {!r.paid && daysOverdue(r) > 0 && <div className="text-xs text-[#E01E37]">{daysOverdue(r)}d overdue</div>}
                    </>
                  ) : <span className="text-[#9A968E] text-sm">—</span>}
                </td>
                <td className="p-4">
                  {r.paid
                    ? <span className="pill pill--paid">Paid</span>
                    : r.paid_amount > 0
                      ? <div className="flex flex-col gap-1" data-testid={`payout-partial-${r.property_id}`}>
                          <span className="pill pill--partial w-fit">Partially paid</span>
                          <span className="text-xs text-[#5C5A58] font-mono-num">Paid {money(r.paid_amount)} of {money(r.amount)}</span>
                        </div>
                      : daysOverdue(r) > 0
                        ? <span className="pill pill--overdue">Overdue</span>
                        : <span className="pill pill--info">Unpaid</span>}
                </td>
                {isAdmin && (
                  <td className="p-4 text-right whitespace-nowrap">
                    {!r.paid && (
                      <Button data-testid={`payout-send-${r.property_id}`} size="sm" onClick={() => openPayout(r)} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white mr-2"><PaperPlaneTilt size={15} className="mr-1" weight="fill" />Pay out</Button>
                    )}
                    {!r.paid && (
                      <Button data-testid={`payout-manual-${r.property_id}`} size="sm" variant="outline" onClick={() => openManual(r)} className="border-[#E9DDC6] mr-2"><CheckCircle size={15} className="mr-1" weight="fill" />Mark paid</Button>
                    )}
                    {!r.paid && (
                      <Button data-testid={`payout-adjust-${r.property_id}`} size="sm" variant="outline" onClick={() => openAdj(r)} className="border-[#E9DDC6] mr-2"><Plus size={15} className="mr-1" />Adjust</Button>
                    )}
                    <Button data-testid={`payout-edit-${r.property_id}`} size="sm" variant="outline" className="border-[#E9DDC6]" onClick={() => openEdit(r)}><PencilSimple size={15} className="mr-1" />Edit</Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PayoutHistory isAdmin={isAdmin} refreshKey={payoutVersion} />

      {isAdmin && <AdminPayoutAccounts />}

      {isAdmin && <PhotographerPayDefaults />}

      {isAdmin && (
        <Dialog open={!!adjRow} onOpenChange={(o) => !o && setAdjRow(null)}>
          <DialogContent data-testid="payout-adjust-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Adjust payout</DialogTitle>
              <DialogDescription>{adjRow?.title} — add an addition or deduction to {adjRow?.photographer}'s payout for this shoot. A reason is required and logged (who, when, why).</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="payout-adjust-type-addition" onClick={() => setAdjType("addition")}
                  className={`rounded-md border py-2 text-sm font-medium transition-colors ${adjType === "addition" ? "bg-[#4A0000] text-white border-[#4A0000]" : "border-[#E9DDC6] text-[#5C5A58]"}`}>Addition (+)</button>
                <button type="button" data-testid="payout-adjust-type-deduction" onClick={() => setAdjType("deduction")}
                  className={`rounded-md border py-2 text-sm font-medium transition-colors ${adjType === "deduction" ? "bg-[#4A0000] text-white border-[#4A0000]" : "border-[#E9DDC6] text-[#5C5A58]"}`}>Deduction (−)</button>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Amount ($)</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                  <Input data-testid="payout-adjust-amount" type="number" min="0" step="0.01" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} className="pl-7" />
                </div>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Reason (required)</p>
                <Input data-testid="payout-adjust-reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="e.g. reshoot deduction, rush bonus, mileage" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" data-testid="payout-adjust-cancel" onClick={() => setAdjRow(null)}>Cancel</Button>
              <Button data-testid="payout-adjust-confirm" disabled={savingAdj} onClick={doAdj} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{savingAdj ? "Applying…" : "Apply adjustment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
          <DialogContent data-testid="payout-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Edit payout</DialogTitle>
              <DialogDescription>{editRow?.title} — adjust the photographer's payout and mark it paid.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Edited (+$30 edit fee)</span>
                <Switch data-testid="payout-edit-edited" checked={form.edited} onCheckedChange={(v) => setForm((f) => ({ ...f, edited: v }))} />
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Add-on ($)</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                  <Input data-testid="payout-edit-bonus" type="number" min="0" step="1" value={form.bonus} onChange={(e) => setForm((f) => ({ ...f, bonus: e.target.value }))} className="pl-7" placeholder="0" />
                </div>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Add-on note</p>
                <Input data-testid="payout-edit-bonus-note" value={form.bonus_note} onChange={(e) => setForm((f) => ({ ...f, bonus_note: e.target.value }))} placeholder="Reason (optional)" />
              </div>
              <div className="flex items-center justify-between border-t border-[#F0ECE2] pt-3">
                <span className="text-sm">Mark payout as paid</span>
                <Switch data-testid="payout-edit-paid" checked={form.paid} onCheckedChange={(v) => setForm((f) => ({ ...f, paid: v }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" data-testid="payout-edit-cancel" onClick={() => setEditRow(null)}>Cancel</Button>
              <Button data-testid="payout-edit-save" disabled={saving} onClick={saveEdit} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Save changes"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog open={!!payoutRow} onOpenChange={(o) => !o && setPayoutRow(null)}>
          <DialogContent data-testid="payout-send-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Pay out photographer</DialogTitle>
              <DialogDescription>Send {payoutRow?.photographer} their pay for {payoutRow?.title} via Stripe, straight to their bank. Edit the amount if needed.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Payout amount ($)</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                  <Input data-testid="payout-send-amount" type="number" min="0" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className="pl-7" />
                </div>
                <p className="text-xs text-[#9A968E] mt-1">Defaults to the full amount shown on this shoot. This also marks the payout as paid.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" data-testid="payout-send-cancel" onClick={() => setPayoutRow(null)}>Cancel</Button>
              <Button data-testid="payout-send-confirm" disabled={payingOut} onClick={doPayout} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white"><PaperPlaneTilt size={16} className="mr-1.5" weight="fill" />{payingOut ? "Sending…" : "Send payout"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog open={!!manualRow} onOpenChange={(o) => !o && setManualRow(null)}>
          <DialogContent data-testid="payout-manual-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Record an offline payment</DialogTitle>
              <DialogDescription>Log a payment you've made (or are making) to {manualRow?.photographer} for {manualRow?.title} outside Stripe. This just records it for your books.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Amount paid ($)</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A58]">$</span>
                  <Input data-testid="payout-manual-amount" type="number" min="0" step="0.01" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} className="pl-7" />
                </div>
                <p className="text-xs text-[#9A968E] mt-1">Defaults to the full amount owed — edit for a partial or custom amount.</p>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Payment method</p>
                <Select value={manualMethod} onValueChange={setManualMethod}>
                  <SelectTrigger data-testid="payout-manual-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Cash", "Check", "Zelle", "Venmo", "PayPal", "Bank transfer", "Other"].map((m) => (
                      <SelectItem key={m} value={m} data-testid={`payout-manual-method-${m}`}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-sm text-[#5C5A58] mb-1">Note (optional)</p>
                <Input data-testid="payout-manual-note" value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="Reference #, memo, etc." />
              </div>
              <div className="flex items-center justify-between border-t border-[#F0ECE2] pt-3">
                <span className="text-sm">This fully settles the shoot</span>
                <Switch data-testid="payout-manual-full" checked={manualFull} onCheckedChange={setManualFull} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" data-testid="payout-manual-cancel" onClick={() => setManualRow(null)}>Cancel</Button>
              <Button data-testid="payout-manual-confirm" disabled={savingManual} onClick={doManual} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white"><CheckCircle size={16} className="mr-1.5" weight="fill" />{savingManual ? "Recording…" : "Record payment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function PayPanel() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  if (!isAdmin) return <PayoutView isAdmin={false} />;
  return (
    <Tabs defaultValue="overview" data-testid="finance-tabs">
      <TabsList className="bg-[#FAF9F6] mb-6 flex-wrap h-auto">
        <TabsTrigger value="overview" data-testid="tab-overview"><ChartLineUp size={16} className="mr-1" />Overview</TabsTrigger>
        <TabsTrigger value="receivables" data-testid="tab-receivables"><ArrowDown size={16} className="mr-1" />Receivables</TabsTrigger>
        <TabsTrigger value="payables" data-testid="tab-payables"><ArrowUp size={16} className="mr-1" />Payables</TabsTrigger>
        <TabsTrigger value="commissions" data-testid="tab-commissions"><HandCoins size={16} className="mr-1" />Commissions</TabsTrigger>
        <TabsTrigger value="schedule" data-testid="tab-schedule"><CalendarCheck size={16} className="mr-1" />Payout schedule</TabsTrigger>
        <TabsTrigger value="discounts" data-testid="tab-discounts"><Ticket size={16} className="mr-1" />Discounts</TabsTrigger>
        <TabsTrigger value="tests" data-testid="tab-tests"><CreditCard size={16} className="mr-1" />Test tools</TabsTrigger>
      </TabsList>
      <TabsContent value="overview"><FinanceOverview /></TabsContent>
      <TabsContent value="receivables"><BillingTable /></TabsContent>
      <TabsContent value="payables"><PayoutView isAdmin={true} /></TabsContent>
      <TabsContent value="commissions"><CommissionsTracker /></TabsContent>
      <TabsContent value="schedule"><PayoutSchedule /></TabsContent>
      <TabsContent value="discounts"><DiscountsPanel /></TabsContent>
      <TabsContent value="tests"><TestPaymentTools isAdmin={true} /></TabsContent>
    </Tabs>
  );
}
